import apiV2Service from 'common/src/modules/api/api-v2.service';
import {clientSettings} from '@cl/shared';
import {
  RemoteAvailableClientSetting,
  RemoteClientSettingValue,
  RemoteUpdateClientSettingsRequest,
  AvailableClientSetting,
  ClientSettingValue,
  ClientSettingUpdate,
} from './clientSettings.types';
import {
  toClientAvailableClientSetting,
  toClientClientSettingValue,
  toRemoteClientSettingUpdate,
} from './clientSettings.dataConverter';

const {clientSettingsValuesPromise, clientSettingsValuesSubject} = clientSettings;

const BASE_PATH = 'ClientSetting';

async function getAvailableClientSettings(): Promise<AvailableClientSetting[]> {
  const {data} = await apiV2Service.executeGetRequest<RemoteAvailableClientSetting[]>(
    `${BASE_PATH}/GetAvailableClientSetting`,
  );

  return data.map(toClientAvailableClientSetting);
}

async function getAvailableClientSettingsForClinchAccount(
  forClientId: number,
): Promise<AvailableClientSetting[]> {
  const {data} = await apiV2Service.executeGetRequest<RemoteAvailableClientSetting[]>(
    `${BASE_PATH}/GetAvailableClientSettingsForClinchAccount`,
    {forClientId},
  );

  return data.map(toClientAvailableClientSetting);
}

async function getClientSettingsValues(): Promise<ClientSettingValue[]> {
  const {data} = await apiV2Service.executeGetRequest<RemoteClientSettingValue[]>(
    `${BASE_PATH}/GetClientSettingsValues`,
  );

  return data.map(toClientClientSettingValue);
}

async function getClientSettingsValuesMap(
  {skipCache} = {skipCache: false},
): Promise<Map<string, string>> {
  if (skipCache) clientSettingsValuesPromise.current = null;

  clientSettingsValuesPromise.current =
    clientSettingsValuesPromise.current ??
    apiV2Service
      .executeGetRequest<RemoteClientSettingValue[]>(`${BASE_PATH}/GetClientSettingsValues`)
      .then((res) => {
        const map = new Map<string, string>();

        res.data.forEach((setting) => {
          if (setting.settingKey && setting.value !== null) {
            map.set(setting.settingKey, setting.value);
          }
        });

        return map;
      })
      .catch((e) => {
        clientSettingsValuesPromise.current = null;
        throw e;
      });

  return clientSettingsValuesPromise.current;
}

async function getClientSettingsValuesForClinchAccount(
  forClientId: number,
): Promise<ClientSettingValue[]> {
  const {data} = await apiV2Service.executeGetRequest<RemoteClientSettingValue[]>(
    `${BASE_PATH}/GetClientSettingsValuesForClinchAccount`,
    {forClientId},
  );

  return data.map(toClientClientSettingValue);
}

async function updateClientSettings(settings: ClientSettingUpdate[]): Promise<void> {
  const remoteSettings = settings.map(toRemoteClientSettingUpdate);
  const request: RemoteUpdateClientSettingsRequest = {
    settings: remoteSettings,
  };

  await apiV2Service.executePutRequest<void>(
    `${BASE_PATH}/UpdateClientSettings`,
    {},
    request as unknown as Record<string, unknown>,
  );

  const freshMap = await getClientSettingsValuesMap({skipCache: true});

  clientSettingsValuesSubject.data = freshMap;
}

async function updateClientSettingsForClinchAccount(
  forClientId: number,
  settings: ClientSettingUpdate[],
): Promise<void> {
  const remoteSettings = settings.map(toRemoteClientSettingUpdate);
  const request: RemoteUpdateClientSettingsRequest = {
    settings: remoteSettings,
  };

  await apiV2Service.executePutRequest<void>(
    `${BASE_PATH}/UpdateClientSettingsForClinchAccount`,
    {forClientId},
    request as unknown as Record<string, unknown>,
  );
}

export default {
  getAvailableClientSettings,
  getAvailableClientSettingsForClinchAccount,
  getClientSettingsValues,
  getClientSettingsValuesMap,
  getClientSettingsValuesForClinchAccount,
  updateClientSettings,
  updateClientSettingsForClinchAccount,
};

